import { useCallback, useEffect, useRef, useState } from 'react'
import { IFRAME_VIEWPORT_PRESETS, PAGE_FACTORS, type IframeElement } from '@praesto/shared'
import { useKeepInView } from '../components/keepInView.js'
import { useText } from '../i18n.js'
import type { CameraStore } from './CameraStore.js'

interface Props {
  element: IframeElement
  selected: boolean
  /** Werkzeuge zeigen? Im Vortrag nicht. */
  canEdit: boolean
  /**
   * Darf ins Board-Dokument geschrieben werden?
   *
   * Bewusst getrennt von `canEdit`: Im Vortrag ist das Bearbeiten aus, der
   * Vortragende darf aber sehr wohl schreiben — und genau davon lebt das
   * geteilte Scrollen. Hingen beide am selben Wert, scrollte er im Vortrag nur
   * für sich, und beim Gast käme nichts an.
   */
  canWrite: boolean
  /** Darf die eingebettete Seite bedient werden? Im streng geführten Modus nicht. */
  allowInteraction: boolean
  camera: CameraStore
  onChange: (patch: Partial<IframeElement>) => void
  onEditUrl: () => void
}

function hostOf(url: string): string {
  try {
    return new URL(url).host
  } catch {
    return url
  }
}

/**
 * Eingebettete Website.
 *
 * Das Element ist das Browserfenster: Die Seite wird bei ihrer eingestellten
 * Breite gerendert und anschließend auf die Elementgröße skaliert. Nicht die
 * Kachelgröße bestimmt also das Layout der Seite — eine kleine Kachel würde sie
 * sonst in ihr Mobil-Layout zwingen.
 *
 * **Gescrollt wird von Præsto, nicht vom iFrame.** Der Rahmen legt die Seite
 * mehrere Fensterhöhen hoch an, beschneidet sie und verschiebt sie. Das ist der
 * einzige Weg, den Scrollstand zu teilen: Den eines fremd-domänigen iFrames kann
 * niemand auslesen oder setzen — Browser verbieten das aus gutem Grund. So
 * dagegen steht der Stand im Board-Dokument und erreicht jeden Gast.
 *
 * Die Fangschicht darüber nimmt das Mausrad entgegen und schützt zugleich das
 * Verschieben des Elements. Wer *in* der Seite klicken will, schaltet per
 * Doppelklick auf direkte Steuerung um — die wirkt dann nur lokal.
 */
export function IframeBody({
  element,
  selected,
  canEdit,
  canWrite,
  allowInteraction,
  camera,
  onChange,
  onEditUrl,
}: Props): React.ReactElement {
  const t = useText()
  const [direct, setDirect] = useState(false)
  const [failed, setFailed] = useState(false)
  /** Eigener Scrollstand für alle, die nicht ins Dokument schreiben dürfen. */
  const [localScroll, setLocalScroll] = useState<number | null>(null)
  const shieldRef = useRef<HTMLDivElement>(null)
  // Die Leiste sitzt über dem Element und rückt herunter, wenn oben kein Platz ist.
  const settingsRef = useKeepInView<HTMLDivElement>(camera)
  const host = hostOf(element.url)

  const live = allowInteraction && direct

  useEffect(() => {
    if (!allowInteraction) setDirect(false)
  }, [allowInteraction])

  // Escape beendet die direkte Steuerung — ohne diesen Ausweg säße man in der
  // Seite fest, sobald der Zeiger sie übernommen hat.
  useEffect(() => {
    if (!direct) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setDirect(false)
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [direct])

  // Bewegt jemand anderes die Seite, folgt die eigene Ansicht wieder.
  useEffect(() => setLocalScroll(null), [element.scrollY])

  const scale = element.viewportWidth > 0 ? element.w / element.viewportWidth : 1
  const windowHeight = scale > 0 ? element.h / scale : element.h
  const pageHeight = windowHeight * Math.max(1, element.pageFactor)
  const maxScroll = Math.max(0, pageHeight - windowHeight)
  const scrollY = Math.min(maxScroll, Math.max(0, localScroll ?? element.scrollY))

  const applyScroll = useCallback(
    (next: number) => {
      const clamped = Math.min(maxScroll, Math.max(0, next))
      // Wer schreiben darf, teilt den Stand; alle anderen scrollen für sich.
      if (canWrite) onChange({ scrollY: clamped })
      else setLocalScroll(clamped)
    },
    [canWrite, maxScroll, onChange],
  )

  /*
   * Das Mausrad wird nativ und nicht-passiv abgefangen.
   *
   * Über React käme es zu spät: Der Canvas hört auf demselben Weg mit und würde
   * zoomen, bevor ein synthetisches `stopPropagation` greift.
   */
  useEffect(() => {
    const shield = shieldRef.current
    if (!shield || maxScroll <= 0) return
    const onWheel = (event: WheelEvent): void => {
      // Zoomen bleibt Zoomen: ⌘/Strg gehört dem Board, nicht der Seite.
      if (event.ctrlKey || event.metaKey) return
      event.preventDefault()
      event.stopPropagation()
      applyScroll(scrollY + event.deltaY / scale)
    }
    shield.addEventListener('wheel', onWheel, { passive: false })
    return () => shield.removeEventListener('wheel', onWheel)
  }, [applyScroll, maxScroll, scale, scrollY])

  if (failed) {
    return (
      <div className="element-iframe-fallback">
        <span className="iframe-host">{host}</span>
        <p>{t('iframe.blocked')}</p>
        <a href={element.url} target="_blank" rel="noreferrer noopener">
          {t('iframe.openTab')}
        </a>
      </div>
    )
  }

  return (
    <>
      {/*
        Die Einstellleiste liegt bewusst *außerhalb* des Rahmens: Der Wrapper
        beschneidet seinen Inhalt (`contain: paint`), damit die skalierte Seite
        wie ein Fenster wirkt — was oberhalb sitzt, wäre sonst weggeschnitten.
      */}
      {selected && canEdit && !live && (
        <div
          className="iframe-settings"
          ref={settingsRef}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <button type="button" onClick={onEditUrl} title={element.url}>
            {host}
          </button>

          <span className="iframe-settings-label">{t('iframe.width')}</span>
          <select
            value={element.viewportWidth}
            onChange={(event) => onChange({ viewportWidth: Number(event.target.value) })}
          >
            {IFRAME_VIEWPORT_PRESETS.map((preset) => (
              <option key={preset.width} value={preset.width}>
                {t(preset.label)} · {preset.width}px
              </option>
            ))}
            {!IFRAME_VIEWPORT_PRESETS.some((p) => p.width === element.viewportWidth) && (
              <option value={element.viewportWidth}>
                {t('iframe.custom')} · {element.viewportWidth}px
              </option>
            )}
          </select>

          <span className="iframe-settings-label">{t('iframe.height')}</span>
          <select
            value={element.pageFactor}
            onChange={(event) => onChange({ pageFactor: Number(event.target.value) })}
            title={t('iframe.heightTitle')}
          >
            {PAGE_FACTORS.map((factor) => (
              <option key={factor} value={factor}>
                {t('iframe.windows', { factor })}
              </option>
            ))}
          </select>

          <button type="button" onClick={() => setDirect(true)} disabled={!allowInteraction}>
            {t('iframe.clickInside')}
          </button>
        </div>
      )}

      <div className="element-iframe-wrap">
        <iframe
          className="media-iframe"
          src={element.url}
          title={host}
          style={{
            width: `${element.viewportWidth}px`,
            height: `${pageHeight}px`,
            // Erst skalieren, dann verschieben: so steht der Scrollwert in den
            // CSS-Pixeln der Seite und bleibt unabhängig vom Board-Zoom.
            transform: `scale(${scale}) translateY(${-scrollY}px)`,
            transformOrigin: '0 0',
          }}
          scrolling="no"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          referrerPolicy="no-referrer"
          loading="lazy"
          onError={() => setFailed(true)}
        />

        {!live && (
          <div
            ref={shieldRef}
            className="element-iframe-shield"
            onDoubleClick={(event) => {
              if (!allowInteraction) return
              event.stopPropagation()
              setDirect(true)
            }}
            title={
              maxScroll > 0
                ? t('iframe.shieldTitle')
                : undefined
            }
          />
        )}

        {live && (
          <button
            className="iframe-release"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => setDirect(false)}
            type="button"
          >
            {t('iframe.release')}
          </button>
        )}

        {/* Zeigt, dass mehr Seite da ist — und wo man gerade steht. */}
        {maxScroll > 0 && !live && (
          <div className="iframe-scrollbar" aria-hidden="true">
            <div
              className="iframe-scrollbar-thumb"
              style={{
                height: `${(windowHeight / pageHeight) * 100}%`,
                top: `${(scrollY / pageHeight) * 100}%`,
              }}
            />
          </div>
        )}
      </div>
    </>
  )
}
